import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'

/* global Chart */

// heartRateData sample times are seconds; Garmin imports store them newest
// first, so sort before plotting against minutes from the first sample.
export const heartRateSeries = (samples) => {
  const usable = (samples || [])
    .filter((sample) => sample && typeof sample.value === 'number' &&
      typeof sample.startDate === 'number')
    .sort((a, b) => a.startDate - b.startDate)
  if (usable.length === 0) {
    return []
  }
  const start = usable[0].startDate
  return usable.map((sample) => ({
    x: Math.round(((sample.startDate - start) / 60) * 100) / 100,
    y: sample.value
  }))
}

// hrSummary_v1.histogram is seconds per fixed-width bpm bin. Trim the empty
// bins at either end so the chart shows the range the swimmer actually used.
export const histogramBins = (histogram) => {
  const seconds = histogram?.seconds || []
  const first = seconds.findIndex((value) => value > 0)
  if (first === -1) {
    return []
  }
  let last = seconds.length - 1
  while (last > first && !(seconds[last] > 0)) {
    last--
  }
  const bins = []
  for (let i = first; i <= last; i++) {
    const low = histogram.minBpm + i * histogram.binWidth
    bins.push({
      label: `${low}–${low + histogram.binWidth - 1}`,
      minutes: Math.round(((seconds[i] || 0) / 60) * 10) / 10
    })
  }
  return bins
}

// Owns one Chart.js instance; a new config object replaces the chart.
const ChartCanvas = ({ config }) => {
  const canvasRef = useRef(null)
  useEffect(() => {
    if (!canvasRef.current || !config) {
      return
    }
    const chart = new Chart(canvasRef.current.getContext('2d'), config)
    return () => chart.destroy()
  }, [config])
  return <div className="detail-chart"><canvas ref={canvasRef} /></div>
}

ChartCanvas.propTypes = {
  config: PropTypes.object
}

const lineConfig = (series) => ({
  type: 'line',
  data: {
    datasets: [{
      label: 'Heart rate',
      data: series,
      borderColor: '#dc3545',
      backgroundColor: 'transparent',
      borderWidth: 1,
      pointRadius: 0,
      lineTension: 0
    }]
  },
  options: {
    maintainAspectRatio: false,
    animation: false,
    legend: { display: false },
    tooltips: { mode: 'nearest', intersect: false },
    scales: {
      xAxes: [{
        type: 'linear',
        scaleLabel: { display: true, labelString: 'Minutes from first sample' }
      }],
      yAxes: [{ scaleLabel: { display: true, labelString: 'bpm' } }]
    }
  }
})

const histogramConfig = (bins) => ({
  type: 'bar',
  data: {
    labels: bins.map((bin) => bin.label),
    datasets: [{
      label: 'Minutes',
      data: bins.map((bin) => bin.minutes),
      backgroundColor: '#dc3545'
    }]
  },
  options: {
    maintainAspectRatio: false,
    animation: false,
    legend: { display: false },
    scales: {
      xAxes: [{ scaleLabel: { display: true, labelString: 'bpm' } }],
      yAxes: [{
        ticks: { beginAtZero: true },
        scaleLabel: { display: true, labelString: 'Minutes' }
      }]
    }
  }
})

const HeartRateChartsComponent = ({ samples, summary }) => {
  const series = useMemo(() => heartRateSeries(samples), [samples])
  const bins = useMemo(() => histogramBins(summary?.histogram), [summary])
  const line = useMemo(() => series.length > 0 ? lineConfig(series) : null, [series])
  const histogram = useMemo(() => bins.length > 0 ? histogramConfig(bins) : null, [bins])

  return (
    <div className="row">
      <div className="col-lg-7 mb-3">
        <div className="detail-label mb-1">Heart rate over time</div>
        {line
          ? <ChartCanvas config={line} />
          : <p className="text-muted mb-0">No heart rate samples</p>}
      </div>
      <div className="col-lg-5 mb-3">
        <div className="detail-label mb-1">Time in heart rate range</div>
        {histogram
          ? <ChartCanvas config={histogram} />
          : <p className="text-muted mb-0">No histogram stored</p>}
      </div>
    </div>
  )
}

HeartRateChartsComponent.propTypes = {
  samples: PropTypes.array,
  summary: PropTypes.object
}

export default HeartRateChartsComponent
